/**
 * Strings for the call-time console. Every key starts with `ct.`. Spread into
 * both dictionaries in web/src/lib/i18n.ts; the `satisfies` below makes a
 * missing or extra Spanish key a type error here.
 */
export const callTimeEn = {
  "ct.title": "Call time",
  "ct.subtitle": "{hours} hours a week. Goal: ${rate} an hour.",
  "ct.subtitleUnset": "Your campaign manager hasn't set a call goal yet.",
  "ct.discipline":
    "Never discuss fundraising with voters. With press: point them to the campaign's next finance report.",
  "ct.start": "Start calling",
  "ct.end": "End",
  "ct.noSession": "Start a session to log calls.",
  "ct.timer": "Time",
  "ct.calls": "Calls",
  "ct.pledgedSession": "Pledged",
  "ct.perHour": "$/hour",
  "ct.goalPerHour": "Goal ${goal}/h",
  "ct.today": "Today",
  "ct.todayOf": "{done} of {goal} min",
  "ct.week": "This week",
  "ct.weekOf": "{done} of {goal} h",
  "ct.streak": "Streak",
  "ct.streakDays": "{n} days",
  "ct.weekRate": "$/hour this week",
  "ct.tab.next": "Next",
  "ct.tab.callbacks": "Callbacks due ({n})",
  "ct.queueEmpty": "Nobody left to call in this list.",
  "ct.later": "Up next",
  "ct.ask": "Ask",
  "ct.owner": "Owner: {name}",
  "ct.lastOutcome": "Last: {outcome}, {when}",
  "ct.callbackAt": "Call back: {when}",
  "ct.noPhone": "No phone on file",
  "ct.call": "Call",
  "ct.pitch": "The ask",
  "ct.guardrail": "Careful",
  "ct.outcome.pledged": "Pledged",
  "ct.outcome.declined": "Declined",
  "ct.outcome.callback": "Call back",
  "ct.outcome.no_answer": "No answer",
  "ct.status.not_called": "Not called",
  "ct.status.callback": "Call back",
  "ct.status.pledged": "Pledged",
  "ct.status.declined": "Declined",
  "ct.status.no_answer": "No answer",
  "ct.status.received": "Received",
  "ct.status.do_not_call": "Do not call",
  "ct.pad.amount": "Pledge amount",
  "ct.pad.confirmPledge": "Log {amount}",
  "ct.pad.review": "Flagged for the treasurer's review ($500 or more).",
  "ct.pad.pledgeNote":
    "A pledge is not a contribution. The treasurer records and reports what is received.",
  "ct.pad.tomorrow9": "Tomorrow 9 am",
  "ct.pad.today6": "Today 6 pm",
  "ct.pad.tomorrow6": "Tomorrow 6 pm",
  "ct.pad.custom": "Another time",
  "ct.pad.confirmCallback": "Save callback",
  "ct.pad.back": "Back",
  "ct.pad.addNote": "+ Note",
  "ct.pad.note": "Short note (no sensitive details)",
  "ct.pad.startFirst": "Start a session first — the timer is what makes $/hour honest.",
  "ct.logged": "Logged: {outcome}. Next call!",
  "ct.loggedPledge": "Pledge of {amount} logged. Next call!",
  "ct.loggedReview": "The treasurer will review it.",
  "ct.correct": "Fix",
  "ct.correcting": "Fixing the last call — choose the right outcome.",
  "ct.sessionEnded": "Session ended: {minutes} min, {calls} calls, {amount} pledged.",
  "ct.money.title": "Toward the goal",
  "ct.money.next": "{date} goal: {amount} raised",
  "ct.money.daysLeft": "{n} days left",
  "ct.money.raised": "Raised (treasurer's report)",
  "ct.money.asOf": "as of {date}",
  "ct.money.toGo": "{amount} to go",
  "ct.money.pending": "Open pledges from calls",
  "ct.money.pendingNote": "Not counted as raised until received.",
  "ct.money.disclaimer":
    "A pledge is not a contribution: it counts as raised only after the treasurer receives it and reports it.",
  "ct.money.noScoreboard": "The treasurer has not entered money raised yet.",
  "ct.money.allTargetsPast": "No money target is set for the days ahead.",
  "ct.cat.title": "By donor category",
  "ct.cat.range": "{pledged} pledged · goal {low}–{high}",
  "ct.noCategories": "Donor groups haven't been set up yet. Ask your treasurer to add them.",
  "ct.loadFailed": "Couldn't load your call list. Check your connection and try again.",
  "ct.manage.title": "Add a prospect",
  "ct.manage.name": "Name",
  "ct.manage.category": "Category",
  "ct.manage.ask": "Ask (USD)",
  "ct.manage.phone": "Phone",
  "ct.manage.email": "Email",
  "ct.manage.employer": "Employer",
  "ct.manage.notes": "Notes",
  "ct.manage.save": "Save prospect",
  "ct.manage.saved": "Prospect saved.",
  "ct.manage.dnc": "Mark do not call",
  "ct.manage.dncDone": "Marked do not call.",
} as const;

export const callTimeEs = {
  "ct.title": "Llamadas a donantes",
  "ct.subtitle": "{hours} horas por semana. Meta: ${rate} por hora.",
  "ct.subtitleUnset": "La gerencia de campaña todavía no ha fijado una meta de llamadas.",
  "ct.discipline":
    "Nunca hables de recaudación con votantes. A la prensa: remítelos al próximo informe de finanzas de la campaña.",
  "ct.start": "Empezar a llamar",
  "ct.end": "Terminar",
  "ct.noSession": "Empieza una sesión para registrar llamadas.",
  "ct.timer": "Tiempo",
  "ct.calls": "Llamadas",
  "ct.pledgedSession": "Comprometido",
  "ct.perHour": "$/hora",
  "ct.goalPerHour": "Meta ${goal}/h",
  "ct.today": "Hoy",
  "ct.todayOf": "{done} de {goal} min",
  "ct.week": "Esta semana",
  "ct.weekOf": "{done} de {goal} h",
  "ct.streak": "Racha",
  "ct.streakDays": "{n} días",
  "ct.weekRate": "$/hora esta semana",
  "ct.tab.next": "Siguiente",
  "ct.tab.callbacks": "Volver a llamar ({n})",
  "ct.queueEmpty": "No queda nadie por llamar en esta lista.",
  "ct.later": "Después",
  "ct.ask": "Pedir",
  "ct.owner": "Responsable: {name}",
  "ct.lastOutcome": "Último: {outcome}, {when}",
  "ct.callbackAt": "Volver a llamar: {when}",
  "ct.noPhone": "Sin teléfono registrado",
  "ct.call": "Llamar",
  "ct.pitch": "La petición",
  "ct.guardrail": "Cuidado",
  "ct.outcome.pledged": "Comprometió",
  "ct.outcome.declined": "No puede",
  "ct.outcome.callback": "Volver a llamar",
  "ct.outcome.no_answer": "No contestó",
  "ct.status.not_called": "Sin llamar",
  "ct.status.callback": "Volver a llamar",
  "ct.status.pledged": "Comprometió",
  "ct.status.declined": "No puede",
  "ct.status.no_answer": "No contestó",
  "ct.status.received": "Recibido",
  "ct.status.do_not_call": "No llamar",
  "ct.pad.amount": "Monto del compromiso",
  "ct.pad.confirmPledge": "Registrar {amount}",
  "ct.pad.review": "Se marcará para revisión del tesorero ($500 o más).",
  "ct.pad.pledgeNote":
    "Un compromiso no es una contribución. El tesorero registra y reporta lo que se recibe.",
  "ct.pad.tomorrow9": "Mañana 9 am",
  "ct.pad.today6": "Hoy 6 pm",
  "ct.pad.tomorrow6": "Mañana 6 pm",
  "ct.pad.custom": "Otra hora",
  "ct.pad.confirmCallback": "Guardar",
  "ct.pad.back": "Atrás",
  "ct.pad.addNote": "+ Nota",
  "ct.pad.note": "Nota breve (sin datos sensibles)",
  "ct.pad.startFirst": "Primero empieza una sesión: el cronómetro hace honesto el $/hora.",
  "ct.logged": "Registrado: {outcome}. ¡A la siguiente!",
  "ct.loggedPledge": "Compromiso de {amount} registrado. ¡A la siguiente!",
  "ct.loggedReview": "El tesorero lo revisará.",
  "ct.correct": "Corregir",
  "ct.correcting": "Corrigiendo la última llamada: elige el resultado correcto.",
  "ct.sessionEnded": "Sesión terminada: {minutes} min, {calls} llamadas, {amount} comprometidos.",
  "ct.money.title": "Hacia la meta",
  "ct.money.next": "Meta del {date}: {amount} recaudados",
  "ct.money.daysLeft": "Faltan {n} días",
  "ct.money.raised": "Recaudado (reporte del tesorero)",
  "ct.money.asOf": "al {date}",
  "ct.money.toGo": "faltan {amount}",
  "ct.money.pending": "Compromisos abiertos de llamadas",
  "ct.money.pendingNote": "No cuentan como recaudado hasta recibirse.",
  "ct.money.disclaimer":
    "Un compromiso no es una contribución: cuenta como recaudado solo cuando el tesorero lo recibe y lo reporta.",
  "ct.money.noScoreboard": "El tesorero aún no ha registrado lo recaudado.",
  "ct.money.allTargetsPast": "No hay una meta de dinero fijada para los próximos días.",
  "ct.cat.title": "Por categoría de donantes",
  "ct.cat.range": "{pledged} comprometido · meta {low}–{high}",
  "ct.noCategories":
    "Los grupos de donantes todavía no están configurados. Pide a tu tesorero que los agregue.",
  "ct.loadFailed":
    "No se pudo cargar tu lista de llamadas. Revisa tu conexión e inténtalo de nuevo.",
  "ct.manage.title": "Agregar prospecto",
  "ct.manage.name": "Nombre",
  "ct.manage.category": "Categoría",
  "ct.manage.ask": "Petición (USD)",
  "ct.manage.phone": "Teléfono",
  "ct.manage.email": "Correo",
  "ct.manage.employer": "Empleador",
  "ct.manage.notes": "Notas",
  "ct.manage.save": "Guardar prospecto",
  "ct.manage.saved": "Prospecto guardado.",
  "ct.manage.dnc": "Marcar no llamar",
  "ct.manage.dncDone": "Marcado como no llamar.",
} satisfies Record<keyof typeof callTimeEn, string>;
